<template>
  <div>
    <navbar :links="links" />
    <!-- Tab-like Buttons to navigate -->
    <tags-navigation-buttons />
    <!-- Header Text -->
    <div class="grey--text text--darken-2 subheading my-3">
      {{ $t('taggableDecodersPageHeaderHint') }}
    </div>
    <v-layout
      v-if="taggableDecoders.length > 0"
      justify-center
      align-center
    >
      <v-layout
        wrap
        :justify-center="$vuetify.breakpoint.smAndUp"
        align-center
      >
        <!-- Reset Table Sorting -->
        <v-slide-x-transition>
          <v-btn
            v-if="showResetTableSortingButton"
            small
            flat
            round
            color="msr"
            class="ml-0 mr-3"
            :title="$t('taggableDecodersResetSortingButtonLabel')"
            @click="resetTableSorting()"
          >
            <v-icon left>
              sort
            </v-icon>
            {{ $t('taggableDecodersResetSortingButtonLabel') }}
          </v-btn>
        </v-slide-x-transition>
        <!-- Tags Filter Menu -->
        <tag-filter-menu
          v-model="selectedTagIds"
          :tags="tags"
          :active="selectedTagIds.length > 0"
          :disabled="loading"
          :title="$t('filterTagsTooltip')"
          class="mr-2"
          :show-tags-management-link="false"
        />
        <!-- Dropdown to change sorting on mobile -->
        <v-menu
          v-if="$vuetify.breakpoint.xs"
          offset-y
          lazy
          :close-on-content-click="false"
        >
          <v-btn
            slot="activator"
            icon
            class="ma-0"
            :class="{
              'grey--text text--darken-1': isUsingDefaultSort,
              'msr--text': !isUsingDefaultSort,
            }"
          >
            <v-icon>sort</v-icon>
          </v-btn>
          <v-list>
            <v-list-tile class="grey--text text--darken-2">
              {{ $t('sortBy') }}
            </v-list-tile>
            <v-divider />
            <v-list-tile
              v-for="header of headers.filter((h) => h.sortable !== false)"
              :key="header.value"
              @click="onMobileSortingDropdownHeaderClicked(header)"
            >
              <v-list-tile-title
                :class="{
                  'msr--text':
                    taggableDecodersPagination.sortBy == header.value,
                }"
              >
                {{ header.text }}
              </v-list-tile-title>
              <v-list-tile-avatar
                v-if="taggableDecodersPagination.sortBy == header.value"
              >
                <v-icon v-if="!taggableDecodersPagination.descending">
                  arrow_upward
                </v-icon>
                <v-icon
                  v-else
                  color="msr"
                >
                  arrow_downward
                </v-icon>
              </v-list-tile-avatar>
            </v-list-tile>
          </v-list>
        </v-menu>
        <!-- Search -->
        <v-flex
          xs12
          md4
        >
          <v-text-field
            v-model="searchText"
            :loading="loading"
            clearable
            class="ml-0"
            prepend-icon="search"
            :placeholder="searchTextPlaceholder"
            :title="$t('search')"
            :hint="searchTextHint"
            persistent-hint
            @keyup.native.esc="searchText = ''"
          />
        </v-flex>
      </v-layout>
    </v-layout>
    <!-- Loading Indicator -->
    <div
      v-if="loading && taggableDecoders.length === 0"
      class="loading-indicator mt-5"
    >
      <v-progress-circular
        :size="50"
        indeterminate
        class="msr--text"
      />
    </div>
    <!-- Taggable Decoders List -->
    <div
      v-if="
        $userprofile &&
          $userprofile.can_use_tagging &&
          taggableDecoders.length > 0
      "
    >
      <!-- DESKTOP: Visualize as Table -->
      <v-data-table
        v-if="$vuetify.breakpoint.smAndUp"
        ref="table"
        :headers="headers"
        :items="filteredDecoders"
        :no-data-text="$t('noTaggableDecodersYet')"
        :no-results-text="$t('noTaggableDecodersFoundForSearch')"
        :pagination.sync="taggableDecodersPagination"
        :search="searchText"
        hide-actions
        must-sort
      >
        <template
          slot="items"
          slot-scope="props"
        >
          <tr
            v-if="
              isGroupedByStore &&
                decoderIdentToShowStoreGroupingHeader[props.item.ident]
            "
            column
          >
            <td colspan="1">
              <!-- Store Grouping Header -->
              <device-site
                :device="props.item"
                class="my-2 mr-5"
              />
            </td>
            <td v-if="$store.getters.userCanSetStoreAssociationMemberNumber">
              <!-- Edit Store Association Membernumber -->
              <div
                :title="$t('storeAssociationMemberNumberEdit')"
                style="margin-top: 2px"
              >
                <decoder-edit-store-association-inline
                  :decoder="props.item"
                  @update="(store) => updateStoreAssociationMemberNumber(store)"
                />
              </div>
            </td>
          </tr>
          <tr>
            <!-- Site -->
            <td>
              <device-site
                v-if="!isGroupedByStore"
                :device="props.item"
              />
            </td>
            <!-- Store Association Membernumber Empty Placeholder -->
            <td v-if="$store.getters.userCanSetStoreAssociationMemberNumber" />
            <!-- Decodertype -->
            <td>
              <device-decodertype :device="props.item" />
            </td>
            <!-- Description -->
            <td>
              <router-link
                class="unstyled-anchor hoverable-icon"
                :to="{
                  name: 'decoder',
                  params: {ctype: props.item.ctype, decoderId: props.item.id},
                }"
                :title="$t('goToDecoderDetailPage')"
              >
                {{ props.item.description }}
              </router-link>
            </td>
            <!-- Created At -->
            <td style="position: relative">
              {{ props.item.created_at | dayjs('DD.MM.YYYY') }}
              <img
                v-if="props.item.is_new"
                :src="$store.state.common.newBadgeURL"
                class="newbadge"
              >
            </td>
            <!-- Tags -->
            <td :class="{'py-2': props.item.tags.length > 0}">
              <!-- List and remove existing -->
              <tag-select-list
                :tags="props.item.tags"
                column
                removable
                removable-tooltip="removeTagFromDecoder"
                @remove="(tag) => deleteTagFromDecoder(tag, props.item)"
              />
              <v-layout align-center>
                <!-- Add new -->
                <tag-select
                  :ref="'desktop-tag-select-' + props.item.ident"
                  button-label=""
                  :button-icon="props.item.ignore_tagging ? 'lock' : 'add'"
                  :button-icon-class="
                    props.item.ignore_tagging ? 'warning--text' : ''
                  "
                  button-text-class="grey--text"
                  :medium="!props.item.ignore_tagging"
                  tooltip="addTagToDecoder"
                  add-tooltip="addTagToDecoder"
                  :show-manage-tags-link="false"
                  :tags="getDropdownTagsForDecoder(props.item)"
                  @add="(tag) => assignTagToDecoder(tag, props.item)"
                >
                  <div slot="extra-tile">
                    <decoder-tag-select-extra-tile
                      :decoder="props.item"
                      @toggle="
                        toggleDecoderIgnoreTagging(
                          props.item,
                          'desktop-tag-select-' + props.item.ident
                        )
                      "
                    />
                    <v-divider v-if="!props.item.ignore_tagging" />
                  </div>
                </tag-select>
              </v-layout>
            </td>
          </tr>
        </template>
      </v-data-table>
      <!-- MOBILE: Visualize as Cards -->
      <v-layout
        v-else
        column
      >
        <v-card
          v-for="decoder of filteredDecoders"
          :key="'decoder-card-' + decoder.ident"
          class="my-2"
        >
          <v-card-title class="pb-0">
            <v-layout
              justify-space-between
              wrap
            >
              <!-- Store/Site -->
              <device-site :device="decoder" />
              <!-- Edit Store Association Membernumber -->
              <div>
                <div class="caption grey--text text--darken-2">
                  {{ $t('storeAssociationMemberNumber') }}
                </div>
                <div slot="input">
                  <decoder-edit-store-association-inline
                    :decoder="decoder"
                    show-icon
                    large
                    @update="
                      (store) => updateStoreAssociationMemberNumber(store)
                    "
                  />
                </div>
              </div>
            </v-layout>
          </v-card-title>
          <v-card-text class="pb-2">
            <v-layout
              align-baseline
              class="mb-3"
              wrap
            >
              <!-- Decodertype -->
              <device-decodertype
                :device="decoder"
                class="mr-3"
              />
              <!-- Description -->
              <router-link
                :to="{
                  name: 'decoder',
                  params: {ctype: decoder.ctype, decoderId: decoder.id},
                }"
                :title="$t('goToDecoderDetailPage')"
              >
                {{ decoder.description }}
              </router-link>
              <!-- Created At -->
              <div class="ml-auto grey--text">
                {{ decoder.created_at | dayjs('DD.MM.YYYY') }}
              </div>
            </v-layout>
            <!-- Tags -->
            <!-- List and remove existing -->
            <tag-select-list
              :tags="decoder.tags"
              column
              removable
              removable-tooltip="removeTagFromDecoder"
              @remove="(tag) => deleteTagFromDecoder(tag, decoder)"
            />
            <!-- Add new -->
            <v-layout
              justify-center
              class="mt-2"
            >
              <tag-select
                :ref="'mobile-tag-selects-' + decoder.ident"
                :button-icon-class="
                  decoder.ignore_tagging ? 'warning--text' : ''
                "
                :button-icon="decoder.ignore_tagging ? 'lock' : 'add'"
                :medium="!decoder.ignore_tagging"
                :show-manage-tags-link="false"
                :tags="getDropdownTagsForDecoder(decoder)"
                add-tooltip="addTagToDecoder"
                button-label=""
                button-text-class="grey--text"
                tooltip="addTagToDecoder"
                @add="(tag) => assignTagToDecoder(tag, decoder)"
              >
                <div slot="extra-tile">
                  <decoder-tag-select-extra-tile
                    :decoder="decoder"
                    @toggle="
                      toggleDecoderIgnoreTagging(
                        decoder,
                        'mobile-tag-selects-' + decoder.ident
                      )
                    "
                  />
                  <v-divider v-if="!decoder.ignore_tagging" />
                </div>
              </tag-select>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-layout>
    </div>
  </div>
</template>

<script>
import orderBy from 'lodash.orderby';

import matchesFilter from '@/matchesFilter';

import {urlQueryParamToBoolean} from '@/utils';
import {addTagToDecoder, removeTagFromDecoder} from '@/mixins/tags';
import tagsMixin from '@/mixins/tags';

import DecoderEditStoreAssociationInline from '@/components/DecoderEditStoreAssociationInline';
import DecoderTagSelectExtraTile from '@/components/DecoderTagSelectExtraTile';
import DeviceDecodertype from '@/components/Device/DeviceDecodertype';
import DeviceSite from '@/components/Device/DeviceSite';
import Navbar from '@/components/Navbar';
import TagFilterMenu from '@/components/TagFilterMenu';
import TagSelect from '@/components/TagSelect';
import TagSelectList from '@/components/TagSelectList';
import TagsNavigationButtons from '@/components/TagsNavigationButtons';

const DEFAULT_SORT_BY_VALUE = 'site_sortkey';

export default {
  components: {
    DecoderEditStoreAssociationInline,
    DecoderTagSelectExtraTile,
    DeviceDecodertype,
    DeviceSite,
    Navbar,
    TagFilterMenu,
    TagSelect,
    TagSelectList,
    TagsNavigationButtons,
  },
  mixins: [tagsMixin],
  data() {
    return {
      links: ['dashboard', {label: 'tags', active: false}, 'taggableDecoders'],
      loading: false,
      // HACK: Tag list of Decoders doesn't rerender immediately by
      //   itself as we modify it within addTagToDecoder() and
      //   removeTagFromDecoder() so we give it a little poke with this.
      //   Likey based on the fact that the original Decoders list
      //   resides in the store, but is copied by v-data-table internals
      //   and our augmentedDecoders list, and we operate on the copies.
      decoderTagsHaveChangedKey: 0,
    };
  },
  computed: {
    selectedTagIds: {
      get() {
        return this.$store.state.common.taggableDecodersSelectedTagIds;
      },
      set(ids) {
        this.$store.commit('SET_TAGGABLE_DECODERS_SELECTED_TAG_IDS', ids);
      },
    },
    headers() {
      return [
        {
          text: this.$t('site'),
          align: 'left',
          value: DEFAULT_SORT_BY_VALUE,
        },
        this.$store.getters.userCanSetStoreAssociationMemberNumber
          ? {
              text: this.isGroupedByStore
                ? this.$t('storeAssociationMemberNumber')
                : '',
              align: 'left',
              value: 'store.association_membernumber',
              sortable: false,
            }
          : null,
        {
          text: this.$t('decoderType'),
          align: 'left',
          value: 'decodertype',
        },
        {
          text: this.$t('description'),
          align: 'left',
          value: 'description',
        },
        {
          text: this.$t('playerCreatedAt'),
          align: 'left',
          value: 'created_at',
        },
        {
          text: this.$t('tags'),
          align: 'left',
          value: 'tags_sortkey',
        },
      ].filter((header) => header !== null);
    },
    tags() {
      return this.$store.state.common.tags;
    },
    tagIdToTag() {
      const idToTag = {};
      for (const tag of this.tags) {
        idToTag[tag.id] = tag;
      }
      return idToTag;
    },
    taggableDecoders() {
      return this.$store.state.common.taggableDecoders || [];
    },
    taggableDecodersPagination: {
      get() {
        return this.$store.state.common.taggableDecodersPagination;
      },
      set(pagination) {
        this.$store.commit('SET_TAGGABLE_DECODERS_PAGINATION', pagination);
      },
    },
    searchText: {
      get() {
        return this.$store.state.common.taggableDecodersSearchText;
      },
      set(text) {
        this.$store.commit('SET_TAGGABLE_DECODERS_SEARCH_TEXT', text);
      },
    },
    searchTextHint() {
      if (!this.tags.length && !this.searchText) {
        return '';
      }
      return `${this.filteredDecoders.length}/${this.taggableDecoders.length}`;
    },
    searchTextPlaceholder() {
      if (!this.taggableDecoders.length) {
        return this.$t('search') + ' ...';
      }
      return this.$t('search') + ` (${this.taggableDecoders.length}) ...`;
    },
    decoderIdentToShowStoreGroupingHeader() {
      // Show grouping header only for first decoder of each Store.
      const storeIdToDecoderIdents = {};
      for (const decoder of this.filteredDecoders) {
        if (!storeIdToDecoderIdents[decoder.store.id]) {
          storeIdToDecoderIdents[decoder.store.id] = [];
        }
        storeIdToDecoderIdents[decoder.store.id].push(decoder.ident);
      }

      const decoderIdentToShowHeader = {};
      for (const storeId of Object.keys(storeIdToDecoderIdents)) {
        const decoderIdents = storeIdToDecoderIdents[storeId];
        for (let i = 0; i < decoderIdents.length; i++) {
          const decoderIdent = decoderIdents[i];
          decoderIdentToShowHeader[decoderIdent] =
            i == 0 && this.isGroupedByStore;
        }
      }
      return decoderIdentToShowHeader;
    },
    isUsingDefaultSort() {
      return (
        this.taggableDecodersPagination.sortBy === DEFAULT_SORT_BY_VALUE &&
        !this.taggableDecodersPagination.descending
      );
    },
    isGroupedByStore() {
      return this.taggableDecodersPagination.sortBy === DEFAULT_SORT_BY_VALUE;
    },
    showResetTableSortingButton() {
      return !this.isGroupedByStore;
    },
    /**
     * Add a bunch of fields useful within the v-data-table template slots.
     */
    augmentedDecoders() {
      return this.taggableDecoders.map((decoder, index) => {
        decoder.index = index + 1;

        // Allow sorting by decoder type.
        decoder.decodertype = decoder.ctype;
        decoder.originalDecodertype = decoder.decodertype;
        if (decoder.tvformat) {
          decoder.decodertype = decoder.decodertype + '_' + decoder.tvformat.id;
        }

        // Allow sorting by site.
        decoder[DEFAULT_SORT_BY_VALUE] =
          decoder.store.city +
          ' ' +
          decoder.store.name +
          ' ' +
          decoder.decodertype +
          ' ' +
          decoder.description +
          ' ' +
          decoder.store.location +
          ' ' +
          decoder.store.address +
          ' ' +
          decoder.store.zipcode;

        // Add Tags list.
        if (decoder.tags === undefined) {
          decoder.tags = [];
          for (const tagId of decoder.tag_ids) {
            const tag = this.tagIdToTag[tagId];
            if (tag) {
              decoder.tags.push(tag);
            }
          }
        }
        decoder.tags = orderBy(decoder.tags, [(tag) => tag.name.toLowerCase()]);
        // We want to put decoders with no tags to the bottom for ASC sorting,
        // then create a merged key from the sorted tag names.
        decoder.tags_sortkey =
          (decoder.tags.length > 0 ? 'a_' : 'z_') +
          decoder.tags
            .map((tag) => tag.name.toLowerCase())
            .sort()
            .join(' ');

        // HACK: If we don't add a dependency on this key, sorting by
        //   tags_sortkey won't trigger recomputation of this list as
        //   needed.
        decoder.decoderTagsHaveChangedKey = this.decoderTagsHaveChangedKey;

        return decoder;
      });
    },
    /**
     * Filter by search text and tags, then sort by chosen sorting column/key.
     */
    filteredDecoders() {
      const search = (this.searchText || '').trim().toLowerCase();
      const words = search.split(' ');

      const filtered = this.augmentedDecoders.filter((decoder) => {
        // Filter by selected Tags.
        let tagsOk = true;
        if (this.selectedTagIds.length > 0) {
          tagsOk =
            decoder.tags.filter((t) => this.selectedTagIds.includes(t.id))
              .length > 0;
        }

        // Filter by search text.
        if (search === '') {
          return tagsOk;
        }
        const decoderTagNames = decoder.tags.map((tag) =>
          tag.name.toLowerCase()
        );
        const searchOk = words.every((word) => {
          return (
            matchesFilter(decoder.decodertype, word) ||
            matchesFilter(decoder.description, word) ||
            matchesFilter(decoder.store.address, word) ||
            matchesFilter(decoder.store.city, word) ||
            matchesFilter(decoder.store.location, word) ||
            matchesFilter(decoder.store.name, word) ||
            matchesFilter(decoder.store.zipcode, word) ||
            matchesFilter(decoder.store.association_membernumber, word) ||
            decoderTagNames.some((tagName) => matchesFilter(tagName, word))
          );
        });
        return tagsOk && searchOk;
      });
      return orderBy(filtered, this.taggableDecodersPagination.sortBy, [
        this.taggableDecodersPagination.descending ? 'desc' : 'asc',
      ]);
    },
  },
  methods: {
    resetTagsFilter() {
      this.selectedTagIds = [];
    },
    onMobileSortingDropdownHeaderClicked(header) {
      if (this.taggableDecodersPagination.sortBy == header.value) {
        this.taggableDecodersPagination.descending =
          !this.taggableDecodersPagination.descending;
      } else {
        this.taggableDecodersPagination.sortBy = header.value;
        this.taggableDecodersPagination.descending = false;
      }
    },
    toggleDecoderIgnoreTagging(decoder, menuRefName) {
      const ignore = !decoder.ignore_tagging;

      if (ignore && decoder.tags.length > 0) {
        const confirmed = confirm(
          this.$t('decoderIgnoreTaggingConfirm', {num: decoder.tags.length})
        );
        if (!confirmed) {
          return;
        }
      }

      let menuRef = null;
      if (menuRefName) {
        menuRef = this.$refs[menuRefName];
        // Handle ref by itself (desktop) VS within a v-for (mobile cards).
        if (menuRefName.startsWith('mobile-tag-selects-')) {
          menuRef = menuRef[0];
        }
      }

      this.$api
        .setDecoderIgnoreTagging(decoder.ident, ignore)
        .then(() => {
          const vm = this;
          // Frontend update
          decoder.ignore_tagging = ignore;

          if (ignore) {
            decoder.tags = [];
            if (menuRef) {
              vm.$nextTick(() => {
                menuRef.close();
              });
            }
          }
        })
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        });
    },
    updateStoreAssociationMemberNumber(store, refetchDecoders = true) {
      // Value has been assigned by the UI already.
      const newAssociationMemberNumber = (
        store.association_membernumber || ''
      ).trim();

      this.$api
        .updateStoreAssociationMemberNumber(
          store.id,
          newAssociationMemberNumber
        )
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: 'storeAssociationMemberNumberEditSuccess',
            translate: true,
          });
          this.$store.commit(
            'SET_STORE_ASSOCIATION_MEMBERNUMBER_IN_SITES_LIST',
            {
              storeId: store.id,
              associationMemberNumber: newAssociationMemberNumber,
            }
          );
        })
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          if (refetchDecoders) {
            this.fetchTaggableDecoders();
          }
        });
    },
    /**
     * Return all Tags that are not yet assigned to the Decoder.
     *
     * Tag Group ID must match Decoder Store content_groups IDs,
     * otherwise they are not compatible and skipped.
     *
     * Sorted by Tag name.
     */
    getDropdownTagsForDecoder(decoder) {
      if (decoder.ignore_tagging) {
        return [];
      }
      const availableTags = this.tags.filter((tag) => {
        const notAlreadyAssigned = !decoder.tags
          .map((decoderTag) => decoderTag.id)
          .includes(tag.id);
        const compatibleByGroupIds = decoder.store.content_groups.includes(
          tag.group
        );
        return notAlreadyAssigned && compatibleByGroupIds;
      });
      return orderBy(availableTags, [(tag) => tag.name.toLowerCase()]);
    },
    assignTagToDecoder(tag, decoder) {
      this.decoderTagsHaveChangedKey += 1;

      addTagToDecoder(tag, decoder).catch((error) => {
        console.error(error);
        this.$bus.$emit('alert-error', {
          message: 'genericFailFeedback',
          translate: true,
        });
      });
    },
    deleteTagFromDecoder(tag, decoder) {
      this.decoderTagsHaveChangedKey += 1;

      removeTagFromDecoder(tag, decoder).catch((error) => {
        console.error(error);
        this.$bus.$emit('alert-error', {
          message: 'genericFailFeedback',
          translate: true,
        });
      });
    },
    resetTableSorting() {
      this.$store.commit('RESET_TAGGABLE_DECODERS_PAGINATION');
    },
    handleDefaultSortFromUrlQueryParams() {
      const sortByNewest =
        !!this.$route.query && urlQueryParamToBoolean(this.$route.query.newest);
      if (!sortByNewest) {
        return;
      }

      console.log('[DEBUG] Sorting so latest players are on top');
      const copiedPagination = Object.assign(
        {},
        this.taggableDecodersPagination
      );
      copiedPagination.sortBy = 'created_at';
      copiedPagination.descending = true;
      this.taggableDecodersPagination = copiedPagination;

      // Remove ?newest=true query param.
      this.$router.replace({...this.$router.currentRoute, query: {}});
    },
    fetchTaggableDecoders({spinner = true} = {}) {
      if (spinner) this.loading = true;
      return this.$store.dispatch('LIST_TAGGABLE_DECODERS').finally(() => {
        if (spinner) this.loading = false;
      });
    },
    fetchTags() {
      this.$store.dispatch('LIST_TAGS');
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.handleDefaultSortFromUrlQueryParams();
      vm.fetchTaggableDecoders();
      vm.fetchTags();
    });
  },
  beforeRouteLeave(to, from, next) {
    this.resetTagsFilter();
    next();
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/TaggableDecoders.vue